import {
  CalendarIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import { Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from '@heroui/modal';
import { Select, SelectItem } from '@heroui/select';
import { addToast } from '@heroui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  useExportJobsControllerCreateMonthlyBillValidationsExport,
  useExportJobsControllerCreateMonthlyCustomerReportsExport,
  useExportJobsControllerCreateMonthlyTariffReviewsExport,
} from '@/api/generated/export-jobs/export-jobs';
import type {
  CreateMonthlyReportExportDtoClassificationsItem,
  CreateMonthlyReportExportDtoStagesItem,
  ServiceTrackerItemDto,
} from '@/api/generated/schemas';
import { SiteDtoBillType, SiteDtoUtilityType } from '@/api/generated/schemas';
import { useSiteListsControllerFindAll } from '@/api/generated/site-lists/site-lists';
import { useSitesControllerGetServiceTrackerSummary } from '@/api/generated/sites/sites';
import {
  DataTable,
  type DataTableColumn,
  ErrorState,
  Icon,
  LoadingSpinner,
  MonthYearPicker,
  PaginationControls,
  SearchInput,
  StatCard,
  UtilityTypeChip,
} from '@/components/common';
import { MonthlyReportExportBanner, USER_EXPORT_JOBS_QUERY_KEY } from '@/components/sites';
import { ServiceStatusChip } from '@/components/sites/ServiceStatusChip';
import {
  SERVICE_STATUS_FILTER_KEYS,
  SERVICE_TYPE_CONFIG,
  type ServiceStatusValue,
} from '@/components/sites/account-management.constants';
import { MONTH_ABBR } from '@/constants';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import { useSearchServiceTracker, useServiceTracker } from '@/queries/service-tracker.queries';
import { formatPeriodLabel } from '@/utils/period';

// Statuses that refer to a future cycle (use *NextPeriod). Everything else
// (Passed/Failed/Sent/etc.) refers to the latest completed record.
const NEXT_CYCLE_STATUSES = new Set(['Due', 'Scheduled', 'Overdue', 'Prepared']);

const periodForStatus = (
  status: string | undefined,
  nextPeriod: { start: string; end: string } | undefined,
  latestPeriod: { start: string; end: string } | undefined,
): string => {
  const period = status && NEXT_CYCLE_STATUSES.has(status) ? nextPeriod : latestPeriod;
  if (period?.start && period?.end) return formatPeriodLabel(period.start, period.end);
  return '-';
};

// Classifications shown in the table chip and filter. UNKNOWN is intentionally
// omitted — the backend sends classification as undefined for unknown sites.
const CLASSIFICATION_LABELS: Record<string, string> = {
  [SiteDtoBillType.CI]: 'C&I',
  [SiteDtoBillType.SME]: 'SME',
  [SiteDtoBillType.EN]: 'EN',
};

const CLASSIFICATION_FILTER_OPTIONS = [SiteDtoBillType.SME, SiteDtoBillType.CI, SiteDtoBillType.EN];

const columns: DataTableColumn<ServiceTrackerItemDto>[] = [
  {
    key: 'site',
    label: 'Site',
    render: (row) => (
      <Link to="/sites/$siteId" params={{ siteId: row.siteId }} onClick={(e) => e.stopPropagation()} className="block">
        <div className="font-mono text-sm text-default-900">{row.connectionPointId}</div>
        {row.supplyAddress && <div className="text-xs text-default-500">{row.supplyAddress}</div>}
      </Link>
    ),
  },
  {
    key: 'company',
    label: 'Company',
    render: (row) => <div className="text-sm text-default-700">{row.companyName ?? '-'}</div>,
  },
  {
    key: 'classification',
    label: 'Type',
    render: (row) =>
      row.classification ? (
        <UtilityTypeChip
          utilityType={row.utilityType as SiteDtoUtilityType | undefined}
          billType={row.classification as SiteDtoBillType}
        />
      ) : (
        <span className="text-xs text-default-400">-</span>
      ),
  },
  {
    key: 'bvPeriod',
    label: 'BV Period',
    width: 150,
    render: (row) => {
      if (!row.billValidationMonths?.length && !row.billValidationNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      return (
        <div className="text-sm whitespace-nowrap">
          {periodForStatus(row.billValidationStatus, row.billValidationNextPeriod, row.billValidationLatestPeriod)}
        </div>
      );
    },
  },
  {
    key: 'bvStatus',
    label: 'BV Status',
    render: (row) => {
      if (!row.billValidationMonths?.length && !row.billValidationNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      if (!row.billValidationStatus) return <span className="text-default-500">-</span>;
      return <ServiceStatusChip status={row.billValidationStatus} />;
    },
  },
  {
    key: 'crPeriod',
    label: 'CR Period',
    width: 150,
    render: (row) => {
      if (!row.customerReportMonths?.length && !row.customerReportNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      return (
        <div className="text-sm whitespace-nowrap">
          {periodForStatus(row.customerReportStatus, row.customerReportNextPeriod, row.customerReportLatestPeriod)}
        </div>
      );
    },
  },
  {
    key: 'crStatus',
    label: 'CR Status',
    render: (row) => {
      if (!row.customerReportMonths?.length && !row.customerReportNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      if (!row.customerReportStatus) return <span className="text-default-500">-</span>;
      return <ServiceStatusChip status={row.customerReportStatus} />;
    },
  },
  {
    key: 'trPeriod',
    label: 'TR Period',
    width: 150,
    render: (row) => {
      if (!row.tariffReviewMonths?.length && !row.tariffReviewNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      return (
        <div className="text-sm whitespace-nowrap">
          {periodForStatus(row.tariffReviewStatus, row.tariffReviewNextPeriod, row.tariffReviewLatestPeriod)}
        </div>
      );
    },
  },
  {
    key: 'trStatus',
    label: 'TR Status',
    render: (row) => {
      if (!row.tariffReviewMonths?.length && !row.tariffReviewNextDue) {
        return <span className="text-xs text-default-400">N/A</span>;
      }
      if (!row.tariffReviewStatus) return <span className="text-default-500">-</span>;
      return <ServiceStatusChip status={row.tariffReviewStatus} />;
    },
  },
];

export const Route = createFileRoute('/')({
  component: Dashboard,
});

function Dashboard() {
  const now = new Date();
  const [contextMonth, setContextMonth] = useState<string>(String(now.getMonth() + 1).padStart(2, '0'));
  const [contextYear, setContextYear] = useState<string>(String(now.getFullYear()));
  const monthPickerModal = useDisclosure();
  const [siteListId, setSiteListId] = useState<string>('');
  const [statuses, setStatuses] = useState<Set<string>>(
    new Set([SERVICE_STATUS_FILTER_KEYS['Due'], SERVICE_STATUS_FILTER_KEYS['Overdue']]),
  );
  const [classifications, setClassifications] = useState<Set<string>>(new Set());

  const pagination = usePaginatedSearch<ServiceTrackerItemDto>({ searchMode: 'cursor' });
  const { resetPagination } = pagination;

  const queryClient = useQueryClient();
  const billValidationsExportMutation = useExportJobsControllerCreateMonthlyBillValidationsExport({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: USER_EXPORT_JOBS_QUERY_KEY }) },
  });
  const customerReportsExportMutation = useExportJobsControllerCreateMonthlyCustomerReportsExport({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: USER_EXPORT_JOBS_QUERY_KEY }) },
  });
  const tariffReviewsExportMutation = useExportJobsControllerCreateMonthlyTariffReviewsExport({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: USER_EXPORT_JOBS_QUERY_KEY }) },
  });

  const exportStages = mapStatusesToStages(statuses);
  // If the user explicitly filtered (statuses non-empty) but every selected
  // status maps to nothing in the export cohort (i.e. only Scheduled), block
  // the export — otherwise we'd silently send no filter and dump everything.
  const isExportBlockedByStageMapping = statuses.size > 0 && exportStages.length === 0;

  const handleExport = (which: 'bill-validations' | 'customer-reports' | 'tariff-reviews') => {
    const targetMonth = `${contextYear}-${contextMonth}`;
    const data = {
      targetMonth,
      stages: exportStages.length > 0 ? exportStages : undefined,
      siteListId: siteListId !== '' ? siteListId : undefined,
      classifications:
        classifications.size > 0
          ? (Array.from(classifications) as CreateMonthlyReportExportDtoClassificationsItem[])
          : undefined,
    };
    const onError = (error: unknown) => {
      addToast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create export',
        color: 'danger',
      });
    };
    if (which === 'bill-validations') {
      billValidationsExportMutation.mutate({ data }, { onError });
    } else if (which === 'customer-reports') {
      customerReportsExportMutation.mutate({ data }, { onError });
    } else {
      tariffReviewsExportMutation.mutate({ data }, { onError });
    }
  };

  const trackerFilters = {
    siteListId: siteListId || undefined,
    dueByMonth: Number(contextMonth),
    dueByYear: Number(contextYear),
    statuses: statuses.size > 0 ? Array.from(statuses).join(',') : undefined,
    classifications: classifications.size > 0 ? Array.from(classifications).join(',') : undefined,
  };

  const paginatedQuery = useServiceTracker({
    ...trackerFilters,
    cursor: pagination.cursor,
    pageSize: pagination.pageSize,
  });
  const searchQuery = useSearchServiceTracker(pagination.debouncedSearch, pagination.searchCursor, trackerFilters);

  const {
    data: rows,
    isLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    handleNextPage,
    handlePreviousPage,
    isFetching,
  } = pagination.withQueries({ paginatedQuery, searchQuery });

  const { data: siteListsData } = useSiteListsControllerFindAll();
  // StatCards intentionally ignore the status filter — the cards' labels
  // ("X Due", "Overdue") already encode a status, so layering the user's
  // filter on top would create contradictions (e.g. Overdue card reading 0
  // while the table shows Overdue chips).
  const { data: summary, isLoading: isSummaryLoading } = useSitesControllerGetServiceTrackerSummary({
    month: Number(contextMonth),
    year: Number(contextYear),
    siteListId: siteListId || undefined,
  });

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold flex-1">Account Management</h1>
        <Button
          color="primary"
          variant="flat"
          size="sm"
          onPress={() => handleExport('bill-validations')}
          isLoading={billValidationsExportMutation.isPending}
          isDisabled={isExportBlockedByStageMapping}
          title={
            isExportBlockedByStageMapping
              ? 'The export cohort never includes "Scheduled" — pick at least one of Due/Overdue/Prepared/Sent/Passed/Failed/etc.'
              : undefined
          }
          startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
        >
          Export Bill Validations
        </Button>
        <Button
          color="primary"
          variant="flat"
          size="sm"
          onPress={() => handleExport('customer-reports')}
          isLoading={customerReportsExportMutation.isPending}
          isDisabled={isExportBlockedByStageMapping}
          title={
            isExportBlockedByStageMapping
              ? 'The export cohort never includes "Scheduled" — pick at least one of Due/Overdue/Prepared/Sent/Passed/Failed/etc.'
              : undefined
          }
          startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
        >
          Export Customer Reports
        </Button>
        <Button
          color="primary"
          variant="flat"
          size="sm"
          onPress={() => handleExport('tariff-reviews')}
          isLoading={tariffReviewsExportMutation.isPending}
          isDisabled={isExportBlockedByStageMapping}
          title={
            isExportBlockedByStageMapping
              ? 'The export cohort never includes "Scheduled" — pick at least one of Due/Overdue/Prepared/Sent/Passed/Failed/etc.'
              : undefined
          }
          startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
        >
          Export Tariff Reviews
        </Button>
        <Button
          variant="bordered"
          size="sm"
          onPress={monthPickerModal.onOpen}
          startContent={<CalendarIcon className="w-4 h-4" />}
        >
          {MONTH_ABBR[Number(contextMonth) - 1]} {contextYear}
        </Button>
        <Modal isOpen={monthPickerModal.isOpen} onClose={monthPickerModal.onClose} size="md">
          <ModalContent>
            <ModalHeader>Select Month</ModalHeader>
            <ModalBody className="pb-6">
              <MonthYearPicker
                selectedMonth={Number(contextMonth)}
                selectedYear={contextYear}
                onMonthChange={(m) => {
                  setContextMonth(String(m).padStart(2, '0'));
                  resetPagination();
                  monthPickerModal.onClose();
                }}
                onYearChange={(y) => {
                  setContextYear(y);
                  resetPagination();
                }}
                color="primary"
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>

      <MonthlyReportExportBanner />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={SERVICE_TYPE_CONFIG.BILL_VALIDATION.icon}
          label={`${SERVICE_TYPE_CONFIG.BILL_VALIDATION.label}s Due`}
          value={summary ? summary.billValidationsDue : '-'}
          variant="success"
          filled={false}
          loading={isSummaryLoading}
        />
        <StatCard
          icon={SERVICE_TYPE_CONFIG.CUSTOMER_REPORT.icon}
          label={`${SERVICE_TYPE_CONFIG.CUSTOMER_REPORT.label}s Due`}
          value={summary ? summary.customerReportsDue : '-'}
          variant="secondary"
          filled={false}
          loading={isSummaryLoading}
        />
        <StatCard
          icon={SERVICE_TYPE_CONFIG.TARIFF_REVIEW.icon}
          label={`${SERVICE_TYPE_CONFIG.TARIFF_REVIEW.label}s Due`}
          value={summary ? summary.tariffReviewsDue : '-'}
          variant="primary"
          filled={false}
          loading={isSummaryLoading}
        />
        <StatCard
          icon={ExclamationTriangleIcon}
          label="Overdue"
          value={summary ? summary.totalOverdue : '-'}
          variant="danger"
          filled={false}
          loading={isSummaryLoading}
        />
      </div>

      <Card>
        <CardBody className="pb-0">
          {/* Search + Filters */}
          <div className="flex items-center gap-4 flex-wrap w-full">
            <div className="w-full sm:max-w-md">
              <SearchInput
                value={pagination.searchQuery}
                onChange={pagination.handleSearchChange}
                placeholder="Search sites..."
              />
            </div>

            <div className="w-64">
              <Select
                label="Site List"
                placeholder="All sites"
                isClearable
                selectedKeys={siteListId ? [siteListId] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setSiteListId(selected ?? '');
                  resetPagination();
                }}
                size="sm"
              >
                {(siteListsData?.data ?? []).map((list) => (
                  <SelectItem key={list.id}>{list.name}</SelectItem>
                ))}
              </Select>
            </div>

            <div className="w-56">
              <Select
                label="Status"
                placeholder="All"
                selectionMode="multiple"
                selectedKeys={statuses}
                onSelectionChange={(keys) => {
                  setStatuses(keys === 'all' ? new Set() : new Set(keys as Set<string>));
                  resetPagination();
                }}
                size="sm"
              >
                {(
                  [
                    'Overdue',
                    'Due',
                    'Scheduled',
                    'Prepared',
                    'Sent',
                    'Passed',
                    'Failed',
                    'In Dispute',
                    'Dispute Resolved',
                  ] as ServiceStatusValue[]
                ).map((status) => (
                  <SelectItem key={SERVICE_STATUS_FILTER_KEYS[status]} textValue={status}>
                    <ServiceStatusChip status={status} />
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="w-48">
              <Select
                label="Type"
                placeholder="All"
                selectionMode="multiple"
                selectedKeys={classifications}
                onSelectionChange={(keys) => {
                  setClassifications(keys === 'all' ? new Set() : new Set(keys as Set<string>));
                  resetPagination();
                }}
                size="sm"
              >
                {CLASSIFICATION_FILTER_OPTIONS.map((value) => (
                  <SelectItem key={value} textValue={CLASSIFICATION_LABELS[value]}>
                    <UtilityTypeChip utilityType={SiteDtoUtilityType.ELECTRICITY} billType={value} />
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>
        </CardBody>

        <CardBody className="pt-0 px-0">
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorState title="Failed to load service tracker data" message="Please try again later" />
          ) : rows.length > 0 ? (
            <>
              <DataTable
                columns={columns}
                data={rows}
                getRowKey={(row) => row.siteId}
                getRowHref={(row) => ({
                  to: `/sites/${row.siteId}`,
                  search: { tab: 'account-management' },
                })}
                emptyContent={
                  <div className="text-center py-12">
                    <p className="text-default-500">No sites with managed services found.</p>
                  </div>
                }
                aria-label="Service tracker"
              />
              <div className="px-4">
                <PaginationControls
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={handleNextPage}
                  onPreviousPage={handlePreviousPage}
                  isLoading={isFetching}
                />
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Icon
                as={pagination.searchQuery ? MagnifyingGlassIcon : TableCellsIcon}
                size="2xl"
                className="text-default-300 mx-auto mb-3"
              />
              <p className="text-default-500">
                {pagination.searchQuery
                  ? 'No sites found matching your search'
                  : 'No sites with managed services found'}
              </p>
              <p className="text-sm text-default-400 mt-1">
                {pagination.searchQuery
                  ? 'Try a different search term'
                  : 'Sites with bill validation or customer report schedules will appear here'}
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Translate the dashboard's uppercase status filter keys (OVERDUE, DUE,
 * IN_DISPUTE, …) into the lowercase stage strings the export endpoint
 * expects (overdue, due, in dispute, …). Drops `SCHEDULED` because
 * scheduled-future records are never in the cohort the export queries.
 */
function mapStatusesToStages(statuses: Set<string>): CreateMonthlyReportExportDtoStagesItem[] {
  return Array.from(statuses)
    .filter((s) => s !== 'SCHEDULED')
    .map((s) => s.toLowerCase().replace(/_/g, ' ') as CreateMonthlyReportExportDtoStagesItem);
}
